/**
 * マスタ管理 — 勘定科目タブ(2026-08-26 再構成)。
 * - 列: コード / 区分(項目) / 科目①(名称) / 説明 / 経費申請(+表示する事業部) / 編集
 * - 各行の +/- で紐づく科目②を展開・格納できる
 * - 説明は行に収まらない分を「…」で省略し、カーソルを合わせると全文表示(title)
 * - 編集フォームはポップアップ(モーダル)で開く(一覧の下に出て見えない問題の解消)
 */
import { useMemo, useState } from 'react';
import Modal from '@core/components/Modal';
import { useMasters } from './MastersProvider';
import { upsertAccount, upsertSubAccount } from './masterRepo';
import AccountImportModal from './AccountImportModal';
import { ITEM_CATEGORIES } from './accountImport';
import { useToast } from '@core/components/Toast';
import { todayStr } from '@core/lib/date';
import type { Account, AccountCategory, DepartmentId, PlLine, SubAccount } from '@core/lib/types';

const CATEGORY_LABELS: Record<AccountCategory, string> = {
  revenue: '収益',
  cogs: '売上原価',
  sgna: '販管費',
  asset: '資産',
  liability: '負債',
};

const PL_LINE_LABELS: Record<string, string> = {
  revenue: '売上',
  cogs: '原価',
  expense: '経費',
  none: '(PL対象外)',
};

/** 区分(項目)。管理番号「会社-項目-略称-年-月-連番」の2要素目
 *  資産・負債は貸借対照表の科目(2026-09-12 勘定科目表の更新で追加)。PLには乗らない
 *  特利・特損は 2027年度予算 v8 の区分(2026-10-23 追加。勘定科目別の予実で「特別利益」「特別損失」に出る) */
const ITEM_OPTIONS: readonly string[] = ITEM_CATEGORIES;

function emptyAccount(): Account {
  return {
    id: '',
    code: '',
    name: '',
    category: 'sgna',
    pl_line: 'expense',
    expense_selectable: true,
    item_category: '販管',
    abbr: '',
    description: '',
    department_ids: null,
    display_order: 9999,
    active: true,
  };
}

export default function AccountsTab() {
  const { accounts, subAccounts, subAccountsOf, reload, departments } = useMasters();
  const toast = useToast();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [editingSub, setEditingSub] = useState<SubAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // 列ソート(2026-08-26): 各列の▲/▼で昇順・降順。未指定は表示順
  const [sortKey, setSortKey] = useState<'order' | 'code' | 'item' | 'name' | 'description' | 'dept'>('order');
  const [sortAsc, setSortAsc] = useState(true);

  const realDeptCount = departments.filter((d) => d.id !== 'sonota').length;
  /** チェックなし=経費申請に出さない(無効)。全チェック=全事業部(2026-08-26 変更) */
  const deptLabel = (a: Account) => {
    const list = a.department_ids ?? [];
    if (list.length === 0) return '非表示(事業部未選択)';
    if (list.length >= realDeptCount) return '全事業部';
    return list.map((id) => departments.find((d) => d.id === id)?.name ?? id).join('・');
  };

  const sortValue = (a: Account): string | number => {
    switch (sortKey) {
      case 'code':
        return a.code ?? '';
      case 'item':
        return a.item_category ?? '';
      case 'name':
        return a.name;
      case 'description':
        return a.description ?? '';
      case 'dept':
        return deptLabel(a);
      case 'order':
        return a.display_order;
    }
  };

  const visibleAccounts = useMemo(
    () =>
      accounts
        .filter((a) => showInactive || a.active)
        .sort((a, b) => {
          const va = sortValue(a);
          const vb = sortValue(b);
          const cmp =
            typeof va === 'number' && typeof vb === 'number'
              ? va - vb
              : String(va).localeCompare(String(vb), 'ja');
          return sortAsc ? cmp : -cmp;
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, showInactive, sortKey, sortAsc, departments],
  );

  /** 列見出しの ▲/▼(クリックで昇順・降順) */
  const SortHead = ({ label, k }: { label: string; k: typeof sortKey }) => (
    <th style={{ whiteSpace: 'nowrap' }}>
      {label}
      <span style={{ marginLeft: 3 }}>
        <button
          className="txn-link"
          style={{ fontSize: 9, padding: 0, color: sortKey === k && sortAsc ? 'var(--brand-blue)' : 'var(--muted)' }}
          title="昇順"
          onClick={() => {
            setSortKey(k);
            setSortAsc(true);
          }}
        >
          ▲
        </button>
        <button
          className="txn-link"
          style={{ fontSize: 9, padding: 0, color: sortKey === k && !sortAsc ? 'var(--brand-blue)' : 'var(--muted)' }}
          title="降順"
          onClick={() => {
            setSortKey(k);
            setSortAsc(false);
          }}
        >
          ▼
        </button>
      </span>
    </th>
  );

  /**
   * CSV出力(2026-09-24 指示)。A列=科目①・B列=科目②の1行1件で書き出す
   * (科目①は科目②の数だけ繰り返す。科目②が無い科目①はB列が空)。
   * 画面の並び順と「無効(旧科目)も表示」の絞り込みをそのまま反映する。Excelでそのまま開ける形式
   */
  function exportCsv() {
    const head = [
      '科目①', '科目②', '科目①コード', '区分', '科目②コード', '説明',
      '経費申請(表示する事業部)', '経費申請に出す', '状態(科目①)', '状態(科目②)',
      '表示順(科目①)', '表示順(科目②)', '科目①ID', '科目②ID',
    ];
    const q = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
    };
    const lines = [head.join(',')];
    let subCount = 0;
    for (const a of visibleAccounts) {
      const base = [
        a.name, '', a.code ?? '', a.item_category ?? CATEGORY_LABELS[a.category], '', a.description ?? '',
        deptLabel(a), a.expense_selectable ? '○' : '—', a.active ? '有効' : '無効', '',
        a.display_order, '', a.id, '',
      ];
      const subs = subAccounts
        .filter((s) => s.account_id === a.id && (showInactive || s.active))
        .sort((x, y) => x.display_order - y.display_order || x.name.localeCompare(y.name, 'ja'));
      if (subs.length === 0) {
        lines.push(base.map(q).join(','));
        continue;
      }
      for (const s of subs) {
        subCount++;
        const row = [...base];
        row[1] = s.name;
        row[4] = s.code ?? '';
        row[9] = s.active ? '有効' : '無効';
        row[11] = s.display_order;
        row[13] = s.id;
        lines.push(row.map(q).join(','));
      }
    }
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `勘定科目一覧_${todayStr()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast(`科目①${visibleAccounts.length}件・科目②${subCount}件をCSVに書き出しました`);
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function saveAccount() {
    if (!editing) return;
    if (!editing.id.trim() || !editing.name.trim()) {
      toast('IDと名称は必須です');
      return;
    }
    setBusy(true);
    try {
      await upsertAccount({ ...editing, id: editing.id.trim() });
      await reload();
      setEditing(null);
      toast('科目①を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /**
   * 経費申請に出す事業部の一括設定(2026-08-31 指示)。
   * true=全事業部に表示 / false=どの事業部にも出さない。無効な科目は対象外
   */
  async function bulkVisibility(show: boolean) {
    const targets = accounts.filter((a) => a.active);
    const ok = window.confirm(
      show
        ? `有効な科目①${targets.length}件を、すべての事業部の経費申請で選べるようにします。よろしいですか?`
        : `有効な科目①${targets.length}件を、経費申請の選択肢から外します(データは消えません)。よろしいですか?`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      const allDepts = departments.map((d) => d.id);
      await Promise.all(
        targets.map((a) => upsertAccount({ ...a, expense_selectable: show, department_ids: show ? allDepts : [] })),
      );
      await reload();
      toast(show ? '全科目を経費申請に表示しました' : '全科目を経費申請から非表示にしました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '一括設定に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function saveSubAccount() {
    if (!editingSub) return;
    if (!editingSub.id.trim() || !editingSub.name.trim()) {
      toast('IDと名称は必須です');
      return;
    }
    setBusy(true);
    try {
      await upsertSubAccount({ ...editingSub, id: editingSub.id.trim() });
      await reload();
      setEditingSub(null);
      toast('科目②を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          勘定科目
          {/* デフォルトはExcel(勘定科目一覧)の並び。▲▼で変更後、ここから戻せる(2026-08-26) */}
          {(sortKey !== 'order' || !sortAsc) && (
            <button
              className="txn-link"
              style={{ marginLeft: 8, fontSize: 12, fontWeight: 400 }}
              onClick={() => {
                setSortKey('order');
                setSortAsc(true);
              }}
            >
              標準の並びに戻す
            </button>
          )}
          <label style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 400, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            無効(旧科目)も表示
          </label>
          {/* CSV出力(2026-09-24 指示)。いま表示している科目①②を、A列=科目①・B列=科目②で書き出す */}
          <button className="txn-link" style={{ fontSize: 12.5, fontWeight: 400 }} onClick={exportCsv}>
            CSV出力
          </button>
          {/* 一括取り込み(2026-10-23): Excel「Gate と v8 の勘定科目の違い」の「取り込み用」シートを貼り付けて追加する */}
          <button className="txn-link" style={{ fontSize: 12.5, fontWeight: 400 }} onClick={() => setImportOpen(true)}>
            一括取り込み
          </button>
          <button className="btn-line" onClick={() => setEditing(emptyAccount())}>
            科目①を追加
          </button>
        </h3>

        {/* 一括操作(2026-08-31 指示): 科目②の開閉と、経費申請に出す/出さないをまとめて切り替える */}
        <div className="toolbar" style={{ marginBottom: 10 }}>
          <span className="tool-filter">
            <span className="hint">科目②</span>
            <button className="chip" onClick={() => setExpanded(new Set(visibleAccounts.map((a) => a.id)))}>
              すべて表示
            </button>
            <button className="chip" onClick={() => setExpanded(new Set())}>
              すべて閉じる
            </button>
          </span>
          <span className="tool-filter">
            <span className="hint">経費申請への表示</span>
            <button className="chip" disabled={busy} onClick={() => void bulkVisibility(true)}>
              全科目を全事業部に表示
            </button>
            <button className="chip" disabled={busy} onClick={() => void bulkVisibility(false)}>
              全科目を非表示
            </button>
          </span>
        </div>
        {/* 指定の列順(2026-08-26): コード/区分/科目①/説明/経費申請/編集 */}
        <table style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '8%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '34%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '8%' }} />
          </colgroup>
          <thead>
            <tr>
              <SortHead label="コード" k="code" />
              <SortHead label="区分" k="item" />
              <SortHead label="科目①(名称)" k="name" />
              <SortHead label="説明" k="description" />
              <SortHead label="経費申請(表示する事業部)" k="dept" />
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleAccounts.map((a) => {
              const subs = subAccountsOf(a.id);
              const open = expanded.has(a.id);
              return [
                <tr key={a.id} style={{ opacity: a.active ? 1 : 0.5 }}>
                  <td className="hint" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.code}
                  </td>
                  <td>{a.item_category ?? CATEGORY_LABELS[a.category]}</td>
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.name}>
                    {/* +/- で科目②を展開・格納 */}
                    <button
                      className="txn-link"
                      style={{ marginRight: 6, fontWeight: 700, width: 18 }}
                      title={open ? '科目②を閉じる' : '科目②を表示'}
                      onClick={() => toggleExpand(a.id)}
                    >
                      {open ? '−' : '+'}
                    </button>
                    {a.name}
                    {subs.length > 0 && (
                      <span className="hint" style={{ marginLeft: 5 }}>
                        ({subs.length})
                      </span>
                    )}
                    {!a.active && <span className="hint">(無効)</span>}
                  </td>
                  {/* 説明: 収まらない分は…で省略し、カーソルを合わせると全文(title) */}
                  <td
                    className="hint"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={a.description ?? ''}
                  >
                    {a.description ?? '—'}
                  </td>
                  {/* 経費申請=表示する事業部(チェックがあれば表示・なければ非表示)。1行・ホバーで全文 */}
                  <td title={deptLabel(a)}>
                    <div
                      className="hint"
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {deptLabel(a)}
                    </div>
                  </td>
                  <td>
                    <button className="txn-link" onClick={() => setEditing({ ...a })}>
                      編集
                    </button>
                  </td>
                </tr>,
                open && (
                  <tr key={`${a.id}-subs`}>
                    {/* 科目②は科目①の下に一段下げて表示(2026-08-31: 階層が分かるように左線+背景) */}
                    <td
                      colSpan={6}
                      style={{
                        background: '#FAFBFD',
                        padding: '8px 14px 12px 34px',
                        borderLeft: '3px solid var(--brand-blue)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <b style={{ fontSize: 12.5 }}>科目②(小科目)— {a.name}</b>
                        <button
                          className="btn-line"
                          style={{ padding: '3px 10px', fontSize: 12 }}
                          onClick={() =>
                            setEditingSub({
                              id: `${a.id}--${crypto.randomUUID().slice(0, 8)}`,
                              account_id: a.id,
                              code: '',
                              name: '',
                              display_order: 9999,
                              active: true,
                            })
                          }
                        >
                          科目②を追加
                        </button>
                      </div>
                      {subs.length === 0 ? (
                        <span className="hint">科目②はまだありません(作成中のExcelが届き次第、取り込みます)。</span>
                      ) : (
                        // 名前そのものを押すと編集できるチップ表示(「編集」リンクの羅列をやめた)
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {subs.map((s) => (
                            <button
                              key={s.id}
                              className="sub-chip"
                              title="クリックで編集"
                              style={{ opacity: s.active ? 1 : 0.5 }}
                              onClick={() => setEditingSub({ ...s })}
                            >
                              {s.name}
                              {!s.active && <span className="hint">(無効)</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
        <p className="hint" style={{ marginTop: 10 }}>
          「+」で科目②を表示します。経費申請の欄は、その科目を選べる事業部です(全事業部=制限なし。編集から設定)。
          区分・略称は管理番号(会社-区分-略称-年-月-連番)に使われます。
        </p>
      </div>

      {/* 科目①の編集(ポップアップ) */}
      {editing && (
        <Modal onClose={() => setEditing(null)} width="min(720px, 94vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>{accounts.some((a) => a.id === editing.id) ? `科目①を編集 — ${editing.name}` : '科目①を追加'}</h3>
          </div>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div>
              <label>ID(後から変更不可)</label>
              <input
                value={editing.id}
                disabled={accounts.some((a) => a.id === editing.id)}
                onChange={(e) => setEditing({ ...editing, id: e.target.value })}
              />
            </div>
            <div>
              <label>コード(科目略称。管理番号 会社-区分-コード-年-月-連番 に使用)</label>
              <input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} />
            </div>
            <div>
              <label>名称</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </div>
            <div>
              <label>区分(管理番号に使用)</label>
              <select
                value={editing.item_category ?? '販管'}
                onChange={(e) => setEditing({ ...editing, item_category: e.target.value })}
              >
                {ITEM_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>PL行(5行契約)— 誤ると全社PLがずれます</label>
              <select
                value={editing.pl_line ?? 'none'}
                onChange={(e) =>
                  setEditing({ ...editing, pl_line: e.target.value === 'none' ? null : (e.target.value as PlLine) })
                }
              >
                {Object.entries(PL_LINE_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="fw">
              <label>説明(申請者向けの補足)</label>
              <input
                value={editing.description ?? ''}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </div>
            <div>
              <label>表示順(小さいほど上。10刻みで間に差し込めます)</label>
              <input
                type="number"
                value={editing.display_order}
                onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })}
              />
            </div>
            <div>
              <label>状態</label>
              <select
                value={editing.active ? '1' : '0'}
                onChange={(e) => setEditing({ ...editing, active: e.target.value === '1' })}
              >
                <option value="1">有効</option>
                <option value="0">無効</option>
              </select>
            </div>
            <div className="fw">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                表示する事業部 — <b style={{ color: 'var(--red)' }}>すべてチェックなしの場合は経費申請に表示されません(無効)</b>
                <button
                  type="button"
                  className="txn-link"
                  style={{ marginLeft: 'auto' }}
                  onClick={() =>
                    setEditing({
                      ...editing,
                      department_ids: departments.filter((d) => d.id !== 'sonota').map((d) => d.id as DepartmentId),
                    })
                  }
                >
                  全選択
                </button>
                <button type="button" className="txn-link" onClick={() => setEditing({ ...editing, department_ids: null })}>
                  全解除
                </button>
              </label>
              {/* チェックボックスと事業部名を密着させ、1事業部=1かたまりで横に並べる(2026-08-26 修正) */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', padding: '8px 0' }}>
                {departments
                  .filter((d) => d.id !== 'sonota')
                  .map((d) => {
                    const list = editing.department_ids ?? [];
                    const checked = list.includes(d.id as DepartmentId);
                    return (
                      <label
                        key={d.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 13,
                          whiteSpace: 'nowrap',
                          width: 'auto',
                          margin: 0,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          style={{ flexShrink: 0, margin: 0, width: 15, height: 15 }}
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...list, d.id as DepartmentId]
                              : list.filter((x) => x !== d.id);
                            setEditing({ ...editing, department_ids: next.length > 0 ? next : null });
                          }}
                        />
                        <span>{d.name}</span>
                      </label>
                    );
                  })}
              </div>
              <p className="hint" style={{ margin: 0 }}>
                例: 「車両系は運送のみ」にしたい場合は運送だけにチェック。対応表のExcelを頂ければ一括取り込みも可能です。
              </p>
            </div>
          </div>
          <button className="btn-green" onClick={() => void saveAccount()} disabled={busy}>
            保存
          </button>
        </Modal>
      )}

      {importOpen && <AccountImportModal onClose={() => setImportOpen(false)} />}

      {/* 科目②の編集(ポップアップ) */}
      {editingSub && (
        <Modal onClose={() => setEditingSub(null)} width="min(560px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>科目②</h3>
          </div>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div>
              <label>コード</label>
              <input value={editingSub.code} onChange={(e) => setEditingSub({ ...editingSub, code: e.target.value })} />
            </div>
            <div>
              <label>名称</label>
              <input value={editingSub.name} onChange={(e) => setEditingSub({ ...editingSub, name: e.target.value })} />
            </div>
            <div>
              <label>表示順</label>
              <input
                type="number"
                value={editingSub.display_order}
                onChange={(e) => setEditingSub({ ...editingSub, display_order: Number(e.target.value) })}
              />
            </div>
            <div>
              <label>状態</label>
              <select
                value={editingSub.active ? '1' : '0'}
                onChange={(e) => setEditingSub({ ...editingSub, active: e.target.value === '1' })}
              >
                <option value="1">有効</option>
                <option value="0">無効</option>
              </select>
            </div>
          </div>
          <button className="btn-green" onClick={() => void saveSubAccount()} disabled={busy}>
            保存
          </button>
        </Modal>
      )}
    </>
  );
}
